import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, CheckCircle, PencilSimple, Trash, Bank } from "@phosphor-icons/react";
import { toast } from "sonner";
import ExpenseFormDialog from "@/components/ExpenseFormDialog";

const fmt = (n, cur) => `${cur === "USD" ? "$" : "MX$"}${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayISO = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const MONTHS_ES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const monthLabel = (ym) => { if(!ym) return ""; const [y,m]=ym.split("-"); return `${MONTHS_ES[parseInt(m,10)-1]} ${y.slice(2)}`; };

/**
 * Standalone page for users who only have the `add_expenses` capability.
 * Lets them register, view, edit and delete their own expenses (with a
 * mandatory justification for edits/deletions, kept for audit).
 */
export default function ExpenseRegister() {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // expense doc being edited
  const [toDelete, setToDelete] = useState(null); // expense doc being deleted
  const [delReason, setDelReason] = useState("");
  const [lastSaved, setLastSaved] = useState(null);
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [expenses, setExpenses] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/expenses", { params: { month } });
      setExpenses(data || []);
    } catch (e) {
      toast.error("No se pudo cargar la lista");
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const onSaved = () => {
    setLastSaved(new Date().toLocaleString("es-MX"));
    setOpen(false);
    setEditing(null);
    load();
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    const reason = delReason.trim();
    if (!reason) { toast.error("La justificación es requerida"); return; }
    try {
      await api.delete(`/expenses/${toDelete.id}`, { params: { reason } });
      toast.success("Gasto eliminado");
      setToDelete(null);
      setDelReason("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const monthTotal = expenses.reduce((s, e) => s + (e.currency === "MXN" ? e.amount : e.amount * 17), 0);

  return (
    <div className="p-6 sm:p-8 space-y-6 max-w-5xl" data-testid="expense-register-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">FACULTAD · REGISTRAR GASTOS</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">Mis gastos</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {expenses.length} gastos en {monthLabel(month)} · Total <b className="numeric">MX${fmt(monthTotal).replace("MX$", "")}</b>
          </p>
        </div>
        <div className="flex gap-2 items-end">
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="w-44 h-9 text-xs"
            data-testid="exp-month-input"
          />
          <Button
            onClick={() => { setEditing(null); setOpen(true); }}
            className="bg-[#002FA7] hover:bg-[#002277] text-white gap-2"
            data-testid="open-expense-form-btn"
          >
            <Plus size={16} weight="bold" /> Nuevo gasto
          </Button>
        </div>
      </header>

      {lastSaved && (
        <div className="inline-flex items-center gap-2 text-xs text-[#00873D] bg-[#00873D]/10 px-3 py-1.5 rounded-sm">
          <CheckCircle size={14} weight="fill" /> Último gasto guardado · {lastSaved}
        </div>
      )}

      {/* Expense list */}
      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="my-expenses-table">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Fecha</th>
              <th className="text-left px-4 py-3">Título</th>
              <th className="text-left px-4 py-3">Categoría</th>
              <th className="text-left px-4 py-3">Proveedor</th>
              <th className="text-left px-4 py-3">Banco</th>
              <th className="text-right px-4 py-3">Monto</th>
              <th className="text-left px-4 py-3">Nota</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {expenses.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-16 text-center text-zinc-400 dotted-bg">Aún no has registrado gastos en este mes</td></tr>
            )}
            {expenses.map((e) => (
              <tr key={e.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`my-exp-row-${e.id}`}>
                <td className="px-4 py-3 text-zinc-600 font-mono-data text-xs">{e.date}</td>
                <td className="px-4 py-3 font-semibold text-zinc-800 max-w-[220px] truncate" title={e.title}>
                  {e.title || <span className="text-zinc-400 font-normal italic">sin título</span>}
                  {Array.isArray(e.edit_history) && e.edit_history.length > 0 && (
                    <span className="ml-2 text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded" title="Este gasto ha sido editado">editado</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-sm" style={{ background: (e.category_color || "#7E57C2") + "20", color: e.category_color || "#7E57C2" }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: e.category_color || "#7E57C2" }} /> {e.category_name}
                  </span>
                </td>
                <td className="px-4 py-3 text-zinc-700">{e.vendor || "—"}</td>
                <td className="px-4 py-3 text-zinc-700 text-xs">
                  {e.bank_name ? <span className="inline-flex items-center gap-1"><Bank size={12} />{e.bank_name}</span> : "—"}
                </td>
                <td className="px-4 py-3 text-right numeric font-semibold text-[#E0000F]">−{fmt(e.amount, e.currency)}</td>
                <td className="px-4 py-3 text-zinc-500 text-xs max-w-[240px] truncate">{e.note || "—"}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <button
                    onClick={() => { setEditing(e); setOpen(true); }}
                    className="p-2 hover:bg-blue-100 text-[#002FA7] rounded"
                    data-testid={`edit-exp-${e.id}`}
                    title="Editar"
                  >
                    <PencilSimple size={14} />
                  </button>
                  <button
                    onClick={() => { setToDelete(e); setDelReason(""); }}
                    className="p-2 hover:bg-red-100 text-[#E0000F] rounded ml-1"
                    data-testid={`del-exp-${e.id}`}
                    title="Eliminar"
                  >
                    <Trash size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ExpenseFormDialog
        open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}
        onSaved={onSaved}
        existing={editing}
      />

      {/* Delete confirmation with mandatory justification */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) { setToDelete(null); setDelReason(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar gasto?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete && <>Se eliminará el gasto de <b>{fmt(toDelete.amount, toDelete.currency)}</b>{toDelete.bank_name ? <> y se regresará el dinero al banco <b>{toDelete.bank_name}</b></> : ""}. Se guardará en el historial de auditoría.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="border border-amber-300 bg-amber-50 rounded p-3 my-2">
            <Label className="text-xs uppercase tracking-wider text-amber-900">Justificación * <span className="font-normal normal-case">(¿por qué eliminas este gasto?)</span></Label>
            <Input
              autoFocus
              value={delReason}
              onChange={(e) => setDelReason(e.target.value)}
              placeholder="Ej. Lo registré por equivocación..."
              className="mt-1 bg-white"
              data-testid="exp-delete-reason"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={!delReason.trim()}
              className="bg-[#E0000F] hover:bg-[#b8000c] text-white"
              data-testid="exp-delete-confirm"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
